import React from "react";
import PropTypes from "prop-types";
import { BsFillTrashFill } from "react-icons/bs";
import { MdModeEditOutline } from "react-icons/md";

// RowActions — the legacy "Action" cell, rebuilt as a presentational component so the
// v2-native Orders and B2B tables carry the same per-row affordances the legacy
// Quotations screen has.
//
// The wrapper and both button class strings are copied verbatim from
// src/components/quotationsTable/table-instance.jsx:29-52. Do NOT "tidy" them:
//   - `.create-order` is a global class from src/styles/Tableinstance.scss (already
//     bundled app-wide); it is what gives the red delete button its text colour.
//   - 1rem is 13px in this app, so px-4/py-2/rounded-2xl do NOT mean what the stock
//     Tailwind scale says. Hand-converting these to px silently changes the geometry.
//   - `bg-white` / `text-white` / `text-md` emit no CSS in this build — never introduce
//     them here.
//
// Legacy rendered the edit affordance as an <a> (hence `cursor-pointer` in its class
// string, which is kept verbatim). Both are real <button type="button"> elements here so
// they are keyboard-reachable and can never submit an enclosing form; the classes,
// icons and icon class strings are unchanged.
//
// Both handlers stop propagation: these rows are clickable — clicking one opens the v2
// side panel — so without stopPropagation every edit/delete would also open the panel
// behind the confirm dialog.
//
// SAFETY / caller contract:
//   - Delete is destructive, so this component itself gates `onDelete` behind
//     window.confirm with legacy's exact wording ("Do you wish to delete the order").
//     `onDelete` is only ever invoked after the user confirms.
//   - The *payment guard* is the CALLER's responsibility, not this component's. Legacy
//     only renders the delete button when the row has no payment in SUCCESS or PENDING
//     (table-instance.jsx:30). This component has no access to the row, so the page must
//     compute that and pass `canDelete={false}` for rows with live payments.
//   - `canDelete={false}` HIDES the delete button entirely rather than disabling it —
//     that is what legacy does (the button is simply not rendered), and a visible-but-
//     disabled destructive control would suggest the action is merely unavailable right
//     now rather than not permitted for this row.
function RowActions({ onEdit, onDelete, canDelete, onBeforeDelete }) {
    const [checking, setChecking] = React.useState(false);

    const handleDelete = async (event) => {
        // Keep the row's own click handler (open side panel) from firing.
        event.stopPropagation();

        // `onBeforeDelete` exists for screens whose row data cannot answer "is this
        // order deletable?" on its own — TAT's rows come from /ops/tat/orders, which
        // carries no `payments`. Rather than drop the guard or pay for a lookup on every
        // visible row, the check runs once, here, when the user actually intends to
        // delete. It resolves to a reason string to block, or null to allow.
        //
        // Deliberately BEFORE the confirm: asking "are you sure?" and only then
        // discovering the order can't be deleted trains people to dismiss the dialog.
        if (onBeforeDelete) {
            setChecking(true);
            let blockedReason = null;
            try {
                blockedReason = await onBeforeDelete();
            } catch (error) {
                // A guard that cannot complete must not open the door.
                blockedReason = "Couldn't verify this order's payments, so it wasn't deleted.";
            } finally {
                setChecking(false);
            }
            if (blockedReason) {
                // eslint-disable-next-line no-alert
                window.alert(blockedReason);
                return;
            }
        }

        // eslint-disable-next-line no-alert
        if (window.confirm("Do you wish to delete the order")) {
            onDelete(event);
        }
    };

    const handleEdit = (event) => {
        event.stopPropagation();
        onEdit(event);
    };

    return (
        <div className="w-full h-full flex flex-row gap-[3px] items-center">
            {canDelete && onDelete && (
                <button
                    type="button"
                    aria-label="Delete"
                    title="Delete"
                    className="px-4 py-2 rounded-2xl font-sans create-order bg-[red]/[0.85] disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={checking}
                    onClick={handleDelete}
                >
                    <BsFillTrashFill />
                </button>
            )}
            {onEdit && (
                <button
                    type="button"
                    aria-label="Edit"
                    title="Edit"
                    className="px-4 py-2 rounded-2xl bg-blue-500 cursor-pointer"
                    onClick={handleEdit}
                >
                    <MdModeEditOutline className=" text-[white] text-xl" />
                </button>
            )}
        </div>
    );
}

RowActions.propTypes = {
    /** Optional async guard run before the confirm; resolve a reason to block, null to allow. */
    onBeforeDelete: PropTypes.func,
    // Pass null explicitly to drop the edit button (a page with no edit destination).
    onEdit: PropTypes.func,
    // Invoked only after the user confirms the window.confirm prompt above.
    onDelete: PropTypes.func,
    // False hides the delete button. The caller must set this from the row's payment
    // state (no SUCCESS/PENDING payment) — see the safety note above.
    canDelete: PropTypes.bool,
};

RowActions.defaultProps = {
    onBeforeDelete: null,
    // No-op defaults rather than null: both legacy buttons render by default, and a
    // caller opts a button out deliberately (onEdit={null}, or canDelete={false}).
    onEdit: () => {},
    onDelete: () => {},
    canDelete: true,
};

export default RowActions;
